// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Form, Button, Icon, Message, Segment } from 'semantic-ui-react';
import { advisorApi, Advisor, AdvisorInvite } from '../api/advisorApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import AdvisorPhotoUpload from '../components/AdvisorPhotoUpload';

function AdvisorFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [advisor, setAdvisor] = useState<Advisor | null>(null);
  const [formData, setFormData] = useState<AdvisorInvite>({
    email: '',
    firstName: '',
    lastName: '',
    brokerageId: '',
    phoneNumber: '',
    licenseNumber: '',
    role: 'USER',
    notes: '',
  });

  useEffect(() => {
    loadBrokerages();
    if (isEditMode && id) {
      loadAdvisor(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadBrokerages = async () => {
    try {
      const data = await brokerageApi.getAll();
      setBrokerages(data.content);
    } catch (err) {
      console.error('Failed to load brokerages:', err);
      setError('Failed to load brokerages');
    }
  };

  const loadAdvisor = async (advisorId: string) => {
    setLoading(true);
    try {
      const advisorData = await advisorApi.getById(advisorId);
      setAdvisor(advisorData);
      setFormData({
        email: advisorData.email,
        firstName: advisorData.firstName,
        lastName: advisorData.lastName,
        brokerageId: advisorData.brokerageId,
        phoneNumber: advisorData.phoneNumber || '',
        licenseNumber: advisorData.licenseNumber || '',
        role: advisorData.role,
        notes: advisorData.notes || '',
      });
    } catch (err) {
      console.error('Failed to load advisor:', err);
      setError('Failed to load advisor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpdate = (photoUrl: string | null) => {
    if (advisor) {
      setAdvisor({ ...advisor, photoUrl: photoUrl || undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode && id) {
        await advisorApi.update(id, { ...formData, status: 'active', role: formData.role } as Advisor);
      } else {
        await advisorApi.invite(formData);
      }
      navigate('/advisors');
    } catch (err) {
      const defaultMessage = isEditMode ? 'Failed to update advisor' : 'Failed to invite advisor';
      setError(err instanceof Error ? err.message : defaultMessage);
    } finally {
      setLoading(false);
    }
  };

  const brokerageOptions = brokerages.map((b) => ({
    key: b.id,
    value: b.id,
    text: b.name,
  }));

  return (
    <Container style={{ marginTop: '50px' }}>
      <Header as='h1'>
        <Icon name={isEditMode ? 'edit' : 'plus'} />
        <Header.Content>
          {isEditMode ? 'Edit Advisor' : 'Invite New Advisor'}
          <Header.Subheader>
            {isEditMode ? 'Update advisor information' : 'Send an invitation to a new advisor'}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Segment>
        <Form onSubmit={handleSubmit} loading={loading}>
          <Form.Group widths='equal'>
            <Form.Field required>
              <label htmlFor='advisor-firstName'>First Name</label>
              <input
                id='advisor-firstName'
                name='firstName'
                value={formData.firstName}
                onChange={handleChange}
                placeholder='John'
                required
              />
            </Form.Field>
            <Form.Field required>
              <label htmlFor='advisor-lastName'>Last Name</label>
              <input
                id='advisor-lastName'
                name='lastName'
                value={formData.lastName}
                onChange={handleChange}
                placeholder='Doe'
                required
              />
            </Form.Field>
          </Form.Group>

          <Form.Field required>
            <label htmlFor='advisor-email'>Email</label>
            <input
              id='advisor-email'
              type='email'
              name='email'
              value={formData.email}
              onChange={handleChange}
              placeholder='john.doe@example.com'
              disabled={isEditMode}
              required
            />
            {isEditMode && (
              <small style={{ color: '#999' }}>Email cannot be changed after creation</small>
            )}
          </Form.Field>

          <Form.Field required>
            <label htmlFor='advisor-brokerageId'>Brokerage</label>
            <select
              id='advisor-brokerageId'
              name='brokerageId'
              value={formData.brokerageId}
              onChange={handleChange}
              disabled={isEditMode}
              required
            >
              <option value=''>Select a brokerage</option>
              {brokerageOptions.map((option) => (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
            {isEditMode && (
              <small style={{ color: '#999' }}>Brokerage cannot be changed after creation</small>
            )}
          </Form.Field>

          <Form.Field required>
            <label htmlFor='advisor-role'>Role</label>
            <select
              id='advisor-role'
              name='role'
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value='USER'>User</option>
              <option value='ADMIN'>Admin</option>
            </select>
            <small style={{ color: '#666' }}>Admins have full access to manage the brokerage</small>
          </Form.Field>

          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='advisor-phoneNumber'>Phone Number</label>
              <input
                id='advisor-phoneNumber'
                type='tel'
                name='phoneNumber'
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder='+14155552671'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='advisor-licenseNumber'>License Number</label>
              <input
                id='advisor-licenseNumber'
                name='licenseNumber'
                value={formData.licenseNumber}
                onChange={handleChange}
                placeholder='LIC-12345'
              />
            </Form.Field>
          </Form.Group>

          <Form.Field>
            <label htmlFor='advisor-notes'>Notes (Internal)</label>
            <textarea
              id='advisor-notes'
              name='notes'
              value={formData.notes}
              onChange={handleChange}
              placeholder='Internal notes about this advisor...'
              rows={3}
            />
          </Form.Field>

          <Button.Group>
            <Button type='button' onClick={() => navigate('/advisors')}>
              Cancel
            </Button>
            <Button.Or />
            <Button positive type='submit' disabled={loading}>
              <Icon name='checkmark' />
              {isEditMode ? 'Update Advisor' : 'Send Invitation'}
            </Button>
          </Button.Group>
        </Form>
      </Segment>

      {isEditMode && id && advisor && (
        <AdvisorPhotoUpload
          advisorId={id}
          currentPhotoUrl={advisor.photoUrl}
          advisorName={`${formData.firstName} ${formData.lastName}`}
          onPhotoUpdate={handlePhotoUpdate}
        />
      )}
    </Container>
  );
}

export default AdvisorFormPage;
